import Vue from 'vue'
import Router from 'vue-router'
import { constantRoutes, notFoundRoute } from './routes'
import { getToken } from '@/utils/auth'
import { flattenRoutes } from '@/utils/routeFilter'
import store from '@/store'

Vue.use(Router)

// 创建基础路由
const createRouter = () => new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  suppressNavigationErrors: true,
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 路由白名单
const whiteList = ['/login', '/404', '/401', '/']

// 动态路由标志
let dynamicRoutesAdded = false

// 导航日志辅助函数
const logNavigation = (type, from, to, details = '') => {
  const prefix = type === 'success' ? '✅' : type === 'error' ? '🚨' : type === 'info' ? 'ℹ️' : '🔄'
  console.log(`${prefix} ${from.path} -> ${to.path} ${details}`)
}

/**
 * 统一角色类型转换
 */
const normalizeRole = (role) => {
  if (role === null || role === undefined || (typeof role === 'string' && role.trim() === '')) {
    return null
  }
  
  if (typeof role === 'string') {
    const numRole = Number(role.trim())
    return isNaN(numRole) ? role.trim() : numRole
  }
  
  if (typeof role === 'number') {
    return role
  }
  
  console.warn(`⚠️ 未知的角色类型: ${typeof role}, 值: ${role}`)
  return String(role)
}



/**
 * 检查用户是否有权限访问当前路由
 */
const hasPermission = (to, userRoles, permissions) => {
  console.log(`🔍 开始权限检查 - 目标路径: ${to.path}, 完整路径: ${to.fullPath}`)
  
  // 白名单路由直接放行
  if (whiteList.includes(to.path)) {
    console.log(`✅ 白名单路由 ${to.path}，直接放行`)
    return true
  }
  
  // 首页路由直接放行，确保能正确重定向到仪表盘
  if (to.path === '/') {
    console.log(`✅ 首页路由 ${to.path}，直接放行`)
    return true
  }
  
  // 检查用户角色
  if (!userRoles || userRoles.length === 0) {
    console.log(`ℹ️ 用户角色为空，暂时允许访问 ${to.path}`)
    return true
  }
  
  // 1. 特殊处理：管理员角色(0)拥有所有权限
  if (userRoles.some(role => normalizeRole(role) === 0)) {
    console.log(`✅ 管理员角色(0)直接放行`)
    return true
  }
  
  const normalizedUserRoles = userRoles.map(normalizeRole).filter(Boolean)
  console.log(`📋 用户角色 (已归一化): ${normalizedUserRoles.join(',')}`)
  
  // 2. 检查路由是否在可访问路由列表中
  const accessibleRoutes = store.getters['permission/routes'] || []
  let routeFound = false
  let matchedRoute = null
  
  function findRoute(routes, parentPath = '') {
    for (const r of routes) {
      const fullPath = parentPath ? `${parentPath}/${r.path}`.replace(/\/{2,}/g, '/') : r.path
      
      if (fullPath === to.path) {
        routeFound = true
        matchedRoute = r
        return true
      }
      if (r.children && r.children.length > 0) {
        if (findRoute(r.children, fullPath)) {
          return true
        }
      }
    }
    return false
  }
  
  findRoute(accessibleRoutes)
  
  if (!routeFound) {
    console.warn(`⚠️ 路由 ${to.path} 不在可访问路由列表中`)
    return false
  }
  
  // 3. 如果路由没有设置权限要求，直接放行
  if (!matchedRoute.meta || !matchedRoute.meta.roles) {
    console.log(`✅ 路由 ${to.path} 无权限要求，直接放行`)
    return true
  }
  
  // 检查路由匹配
  const matched = to.matched || []
  console.log(`📋 路由匹配情况 - 路径: ${to.path}, 匹配路由数: ${matched.length}`)
  
  // 检查所有已注册的路由
  const allRoutes = [
    ...constantRoutes,
    ...(store.state.permission.addRoutes || []),
    notFoundRoute
  ]
  
  // 递归收集所有路由路径（包括嵌套路由）
  function collectAllPaths(routes, parentPath = '') {
    return routes.flatMap(route => {
      // 构建完整路径，处理相对路径
      const fullPath = parentPath ? `${parentPath}/${route.path}`.replace(/\/{2,}/g, '/') : route.path
      const paths = [fullPath]
      
      if (route.children && route.children.length > 0) {
        // 递归收集子路由路径
        paths.push(...collectAllPaths(route.children, fullPath))
      }
      
      return paths
    })
  }
  
  const allRoutePaths = collectAllPaths(allRoutes).filter(path => path && path !== '*')
  
  console.log(`📋 所有已注册路由路径: ${allRoutePaths.join(', ')}`)
  
  if (matched.length === 0) {
    console.log('❌ 路由匹配失败！')
    console.log('🔍 查找匹配路由...')
    
    // 尝试手动匹配路由
    const potentialMatches = allRoutes.filter(route => {
      if (route.path === to.path) return true
      if (route.path === '*') return false
      if (route.path.includes(':')) {
        const regex = new RegExp(`^${route.path.replace(/:\w+/g, '[^/]+')}$`)
        return regex.test(to.path)
      }
      return false
    })
    
    console.log(`🔍 潜在匹配的路由: ${JSON.stringify(potentialMatches.map(r => r.path))}`)
    
    // 检查路径格式
    const pathSegments = to.path.split('/').filter(Boolean)
    console.log(`🔍 路径分段: ${JSON.stringify(pathSegments)}`)
    
    // 检查是否是动态路由未正确添加
    const isDynamicRoute = pathSegments.some(segment => segment.startsWith(':'))
    console.log(`🔍 是否动态路由: ${isDynamicRoute}`)
    
    logNavigation('error', { path: '' }, to, '路由匹配失败')
    
    // 不要直接返回false，而是尝试查看是否有匹配的路由
    // 先检查权限，再处理匹配问题
    const allRoutesFlat = flattenRoutes(allRoutes)
    
    // 查找与目标路径匹配的路由
    const matchingRoute = allRoutesFlat.find(route => route.fullPath === to.path)
    if (matchingRoute) {
      console.log(`✅ 找到匹配的路由: ${matchingRoute.fullPath}`)
      // 对找到的路由进行权限检查
      const meta = matchingRoute.meta || {}
      
      // 检查角色权限
      if (meta.roles && meta.roles.length) {
        const normalizedRouteRoles = meta.roles.map(normalizeRole).filter(Boolean)
        const hasRole = normalizedUserRoles.some(userRole => 
          normalizedRouteRoles.some(routeRole => {
            const userRoleNum = Number(userRole)
            const routeRoleNum = Number(routeRole)
            
            if (!isNaN(userRoleNum) && !isNaN(routeRoleNum)) {
              return userRoleNum === routeRoleNum
            }
            
            return String(userRole) === String(routeRole)
          })
        )
        
        if (!hasRole) {
          console.log(`❌ 角色权限不足: ${normalizedUserRoles.join(',')} 不在允许的角色 ${normalizedRouteRoles.join(',')} 中`)
          return false
        } else {
          console.log(`✅ 角色权限检查通过`)
        }
      }
      
      // 检查权限码
      if (meta.permissions && meta.permissions.length) {
        if (!permissions || !permissions.length) {
          console.log('❌ 权限码不足')
          return false
        }
        
        const hasPermission = meta.permissions.some(permission => permissions.includes(permission))
        if (!hasPermission) {
          console.log('❌ 权限码不匹配')
          return false
        } else {
          console.log('✅ 权限码检查通过')
        }
      }
      
      console.log(`✅ 权限检查通过，但路由匹配有问题`)
      return true
    } else {
      console.log(`❌ 没有找到匹配的路由`)
      return false
    }
  }
  
  // 权限检查逻辑
  console.log('🔍 开始匹配路由的权限检查...')
  return matched.every(routeRecord => {
    const meta = routeRecord.meta || {}
    console.log(`📋 检查路由记录: ${routeRecord.path}, 角色要求: ${JSON.stringify(meta.roles || [])}`)
    
    // 检查角色权限
    if (meta.roles && meta.roles.length) {
      const normalizedRouteRoles = meta.roles.map(normalizeRole).filter(Boolean)
      console.log(`📋 路由角色 (已归一化): ${normalizedRouteRoles.join(',')}`)
      
      const hasRole = normalizedUserRoles.some(userRole => 
        normalizedRouteRoles.some(routeRole => {
          const userRoleNum = Number(userRole)
          const routeRoleNum = Number(routeRole)
          
          if (!isNaN(userRoleNum) && !isNaN(routeRoleNum)) {
            return userRoleNum === routeRoleNum
          }
          
          return String(userRole) === String(routeRole)
        })
      )
      
      if (!hasRole) {
        console.log(`❌ 角色权限不足: ${normalizedUserRoles.join(',')} 不在允许的角色 ${normalizedRouteRoles.join(',')} 中`)
        return false
      } else {
        console.log(`✅ 角色权限检查通过`)
      }
    } else {
      console.log(`✅ 该路由记录没有角色要求`)
    }
    
    // 检查权限码
    if (meta.permissions && meta.permissions.length) {
      if (!permissions || !permissions.length) {
        console.log('❌ 权限码不足')
        return false
      }
      
      const hasPermission = meta.permissions.some(permission => permissions.includes(permission))
      if (!hasPermission) {
        console.log('❌ 权限码不匹配')
        return false
      } else {
        console.log('✅ 权限码检查通过')
      }
    } else {
      console.log(`✅ 该路由记录没有权限码要求`)
    }
    
    return true
  })
}

// 本地存储用户信息管理
const userStorage = {
  // 尝试从本地存储恢复用户信息
  async restore() {
    try {
      const cachedUserInfo = localStorage.getItem('user_info')
      if (!cachedUserInfo) {
        logNavigation('info', { path: '' }, { path: '' }, '本地存储中没有用户信息')
        return false
      }
      
      const userInfo = JSON.parse(cachedUserInfo)
      store.commit('user/SET_USER_INFO', userInfo)
      
      // 恢复角色信息
      const cachedRoles = localStorage.getItem('user_roles')
      let roles = []
      
      if (cachedRoles) {
        try {
          roles = JSON.parse(cachedRoles).map(normalizeRole).filter(Boolean)
        } catch (e) {
          console.warn('解析缓存的角色列表失败:', e)
          // 尝试从单一角色缓存恢复
          const cachedUserRole = localStorage.getItem('user_role')
          if (cachedUserRole) {
            const role = normalizeRole(cachedUserRole)
            if (role) roles = [role]
          }
        }
      }
      
      if (roles.length > 0) {
        store.commit('user/SET_ROLES', roles)
      }
      
      logNavigation('success', { path: '' }, { path: '' }, `用户信息恢复成功，角色: ${roles.join(',')}`)
      return true
    } catch (error) {
      console.error('恢复用户信息失败:', error)
      return false
    }
  },
  
  // 保存用户信息到本地存储
  save() {
    try {
      const userInfo = store.getters['user/userInfo']
      const roles = store.getters['user/roles']
      
      if (userInfo) {
        localStorage.setItem('user_info', JSON.stringify(userInfo))
        
        if (roles && roles.length > 0) {
          const rolesAsStrings = roles.map(String)
          localStorage.setItem('user_roles', JSON.stringify(rolesAsStrings))
          localStorage.setItem('user_role', String(roles[0]))
        }
      }
    } catch (error) {
      console.error('保存用户信息到本地存储失败:', error)
    }
  },
  
  // 清除本地存储的用户信息
  clear() {
    try {
      localStorage.removeItem('user_info')
      localStorage.removeItem('user_role')
      localStorage.removeItem('user_roles')
    } catch (error) {
      console.error('清除本地存储用户信息失败:', error)
    }
  }
}

/**
 * 确保动态路由已添加
 * @returns {Promise<boolean>} 是否添加了新的路由
 */
const ensureDynamicRoutes = async () => {
  // 如果已添加，直接返回
  if (dynamicRoutesAdded) {
    return false
  }
  
  try {
    let roles = store.getters['user/roles']
    
    // 如果用户角色为空，获取用户信息
    if (!roles || roles.length === 0) {
      logNavigation('info', { path: '' }, { path: '' }, '等待用户信息获取...')
      await store.dispatch('user/getUserInfo')
      roles = store.getters['user/roles']
    }
    
    // 生成动态路由
    const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
    
    // 检查生成的路由
    console.log('🔍 生成的动态路由:', accessRoutes)
    
    // 手动添加动态路由到路由实例中
    if (accessRoutes && accessRoutes.length > 0) {
      router.addRoutes(accessRoutes)
      logNavigation('success', { path: '' }, { path: '' }, `手动添加动态路由完成，路由数量: ${accessRoutes.length}`)
    } else {
      console.warn('⚠️ 没有生成任何动态路由')
    }
    
    // 添加404路由（确保是最后一个）
    router.addRoutes([notFoundRoute])
    
    dynamicRoutesAdded = true
    
    // 检查路由实例中的所有路由
    console.log('📋 路由实例中的所有路由:', router.getRoutes())
    
    if (accessRoutes && accessRoutes.length > 0) {
      logNavigation('success', { path: '' }, { path: '' }, `动态路由添加完成，路由数量: ${accessRoutes.length}`)
      return true
    } else {
      logNavigation('info', { path: '' }, { path: '' }, '没有需要添加的动态路由')
      return false
    }
  } catch (error) {
    console.error('确保动态路由失败:', error)
    // 出错也标记，防止死循环
    dynamicRoutesAdded = true
    return false
  }
}



// 获取所有路由（包括动态添加的）
function getAllRoutes() {
  const routes = []
  
  // 从根路由开始收集
  function collectRoutes(route, parentPath = '') {
    const fullPath = parentPath + (parentPath && !route.path.startsWith('/') ? '/' : '') + route.path
    routes.push({
      path: fullPath,
      name: route.name,
      component: route.component ? route.component.name : '匿名组件'
    })
    
    if (route.children && route.children.length > 0) {
      route.children.forEach(child => collectRoutes(child, fullPath))
    }
  }
  
  // 遍历所有根路由（包括动态添加的）
  router.getRoutes().forEach(route => collectRoutes(route))
  
  return routes
}



// 全局监听导航结果
router.afterEach((to, from, failure) => {
  if (failure) {
    if (failure.type === 'cancelled') {
      // 导航取消是正常现象，简化日志输出
      logNavigation('info', from, to, `导航取消 (${failure.type})`)
    } else {
      // 其他类型的导航失败
      console.group(`🚨 导航失败: ${failure.type}`)
      console.log('从:', from.fullPath)
      console.log('到:', to.fullPath)
      console.log('详情:', failure)
      console.log('所有注册的路由:', getAllRoutes())
      console.groupEnd()
    }
  } else {
    // 导航成功
    logNavigation('success', from, to)
  }
})

// 路由守卫优化版
router.beforeEach(async (to, from, next) => {
  logNavigation('info', from, to, '路由守卫开始')
  
  // 设置页面标题
  if (to.meta?.title) {
    document.title = `${to.meta.title} - 管理系统`
  }

  const hasToken = getToken()
  logNavigation('info', from, to, `Token状态: ${hasToken ? '存在' : '不存在'}`)
  
  if (hasToken) {
    // 已登录状态下访问登录页，重定向到首页
    if (to.path === '/login') {
      next({ path: '/', replace: true })
      return
    }

    try {
      // 获取当前用户信息
      let hasUserInfo = store.getters['user/userInfo']?.id
      let roles = store.getters['user/roles'] || []
      
      // 如果用户信息不完整，尝试恢复或获取
      if (!hasUserInfo || roles.length === 0) {
        const restored = await userStorage.restore()
        
        if (!restored) {
          logNavigation('info', from, to, '从API获取用户信息...')
          await store.dispatch('user/getUserInfo')
          userStorage.save()
        }
        
        hasUserInfo = store.getters['user/userInfo']?.id
        roles = store.getters['user/roles'] || []
      }
      
      const currentPermissions = store.getters['user/permissions'] || []
      
      // 确保动态路由已添加
      if (!dynamicRoutesAdded) {
        logNavigation('info', from, to, '动态路由未添加，正在处理...')
        await ensureDynamicRoutes()
        // Vue Router 3.x 中 addRoutes 是同步的，直接重定向即可
        next({ ...to, replace: true })
        return
      }
      
      // 权限检查
      const canAccess = hasPermission(to, roles, currentPermissions)
      
      if (canAccess) {
        next()
      } else {
        // 无权限处理
        const redirectPath = to.matched.length === 0 ? '/404' : '/401'
        logNavigation('info', from, to, `无权限访问，重定向到${redirectPath}`)
        next({ path: redirectPath, replace: true })
      }
      
    } catch (error) {
      console.error('路由守卫错误:', error)
      userStorage.clear()
      
      // 尝试刷新token
      if (error.response?.status === 401) {
        try {
          const refreshed = await store.dispatch('user/refreshToken')
          if (refreshed) {
            logNavigation('success', from, to, 'Token刷新成功，重试路由')
            next({ ...to, replace: true })
            return
          }
        } catch (refreshError) {
          console.error('刷新token失败:', refreshError)
        }
      }
      
      // 清除用户信息并跳转登录页
      await store.dispatch('user/logout')
      next(`/login?redirect=${encodeURIComponent(to.path)}`)
    }
  } else {
    // 无Token处理
    // 优先处理根路径
    if (to.path === '/') {
      logNavigation('info', from, to, '无Token用户访问根路径，重定向到登录页')
      next({ path: '/login', replace: true })
    } else if (whiteList.includes(to.path)) {
      // 白名单路径直接放行
      next()
    } else {
      // 非白名单路径重定向到登录页
      next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
    }
  }
})

// 路由错误处理
router.onError((error) => {
  // 过滤掉导航取消的错误信息
  if (error.name === 'NavigationCanceledError' || error.message.includes('Navigation cancelled')) {
    return
  }
  
  console.error('路由加载错误:', error)
  
  // 处理chunk加载失败
  if (/Loading chunk (\d)+ failed/.test(error.message)) {
    const targetPath = router.history.pending.fullPath
    if (targetPath) {
      window.location.href = targetPath
    }
  }
})

// 监听用户信息变化
store.subscribe((mutation) => {
  if (mutation.type.startsWith('user/')) {
    const userMutations = [
      'user/SET_USER_INFO',
      'user/SET_ROLES',
      'user/SET_PERMISSIONS'
    ]
    
    if (userMutations.includes(mutation.type)) {
      // 使用setTimeout确保状态更新完成
      setTimeout(() => userStorage.save(), 100)
    }
  }
})

// 重置路由
export const resetRouter = () => {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
  dynamicRoutesAdded = false
  store.commit('permission/RESET_ROUTES')
}

// 页面刷新时初始化路由
export const initRouterOnRefresh = async () => {
  const hasToken = getToken()
  
  if (hasToken) {
    try {
      logNavigation('info', { path: '' }, { path: '' }, '页面刷新，初始化路由...')
      
      // 恢复用户信息
      const restored = await userStorage.restore()
      
      if (restored) {
        const roles = store.getters['user/roles']
        
        // 立即生成并添加动态路由
        if (!dynamicRoutesAdded) {
          logNavigation('info', { path: '' }, { path: '' }, '页面刷新时生成动态路由...')
          const accessRoutes = await store.dispatch('permission/generateRoutes', roles)
          
          if (accessRoutes && accessRoutes.length > 0) {
            router.addRoutes(accessRoutes)
            dynamicRoutesAdded = true
            logNavigation('success', { path: '' }, { path: '' }, '页面刷新时动态路由添加完成')
          }
        }
      }
    } catch (error) {
      console.error('页面刷新初始化路由失败:', error)
    }
  }
}

export default router
